import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';

import Tab from './Tab';
import './Tabs.scss';

export default function Tabs({
  tabs,
  selectedTab,
  handleTabChange,
  tabPanelId,
  tablistLabel,
  onEnterMoveToPanelContent,
}) {
  const tabRefs = useRef([]);

  const handleTabKeyDown = useCallback(
    (e, index) => {
      if (e.key === 'Enter' || e.key === ' ') {
        if (typeof onEnterMoveToPanelContent === 'function') {
          e.preventDefault();
          const tabName = tabs[index].name;
          const syncSelectionThenFocus = () => {
            onEnterMoveToPanelContent();
          };
          if (tabName !== selectedTab) {
            handleTabChange(tabName);
            setTimeout(syncSelectionThenFocus, 0);
          } else {
            syncSelectionThenFocus();
          }
          return;
        }
      }
      const len = tabs.length;
      let nextIndex = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = (index + 1) % len;
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = (index - 1 + len) % len;
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = len - 1;
      }
      if (nextIndex === null) {
        return;
      }
      const nextTab = tabs[nextIndex];
      handleTabChange(nextTab.name);
      requestAnimationFrame(() => {
        const el = tabRefs.current[nextIndex];
        if (el && typeof el.focus === 'function') {
          el.focus();
        }
      });
    },
    [tabs, selectedTab, handleTabChange, onEnterMoveToPanelContent],
  );

  return (
    <ul
      className="tabs mw8 mt3 w-100 center"
      role="tablist"
      aria-label={tablistLabel}
    >
      {tabs.map((tab, i) => {
        const cn = tab.name === selectedTab ? 'selected' : '';

        const badgeCount =
          Object.hasOwn(tab, 'newCount') && typeof tab.newCount === 'number'
            ? tab.newCount
            : undefined;

        return (
          <Tab
            key={tab.name}
            label={tab.label}
            name={tab.name}
            className={cn}
            handleTabChange={handleTabChange}
            isSelected={tab.name === selectedTab}
            tabIndex={0}
            id={`tab-${tab.name}`}
            panelId={tabPanelId}
            onKeyDown={(e) => handleTabKeyDown(e, i)}
            innerRef={(el) => {
              tabRefs.current[i] = el;
            }}
            badgeCount={badgeCount}
          />
        );
      })}
    </ul>
  );
}

Tabs.propTypes = {
  tabs: PropTypes.array,
  selectedTab: PropTypes.string,
  handleTabChange: PropTypes.func,
  tabPanelId: PropTypes.string,
  tablistLabel: PropTypes.string,
  onEnterMoveToPanelContent: PropTypes.func,
};

Tabs.defaultProps = {
  tabs: [],
  selectedTab: '',
  handleTabChange: () => {},
  tabPanelId: '',
  tablistLabel: 'Tasks',
  onEnterMoveToPanelContent: undefined,
};
